import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { formatCurrencyAmount } from '@/utils/currency'
import GolfCourseSplitLayout from '../components/GolfCourseSplitLayout'
import BackPillButton from '../components/BackPillButton'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { normalizeDateKey } from '../utils/golfTime'

function buildPricingLookup(pricing = []) {
  const normalized = { member: {}, visitor: {} }

  pricing.forEach((row) => {
    const audience = row?.is_member === false ? 'visitor' : 'member'
    const holes = Number.parseInt(row?.holes, 10)
    const dayKey = row?.day_type === 'weekend' ? 'weekend' : 'weekday'
    const timeKey = row?.time_band === 'offpeak' ? 'offpeak' : 'peak'
    if (![9, 18].includes(holes)) return

    const rawPrice = row?.price
    if (rawPrice === null || rawPrice === undefined || rawPrice === '') return

    const parsedPrice = Number(rawPrice)
    if (!Number.isFinite(parsedPrice)) return

    if (!normalized[audience][holes]) {
      normalized[audience][holes] = { weekday: {}, weekend: {} }
    }

    normalized[audience][holes][dayKey][timeKey] = parsedPrice
  })

  return normalized
}

function RateCell({ value, currencyCode }) {
  const { locale } = useOptionalPrivanaI18n()
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return <span className="mp-rate-value muted">—</span>
  }

  return <span className="mp-rate-value">{formatCurrencyAmount(value, currencyCode, { locale })}</span>
}

export default function MemberGolfCourseRatesPage({ clubCode }) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const { courseId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet } = useMemberPortalApi()

  const initialCourse = location.state?.course || null
  const [course, setCourse] = useState(initialCourse)
  const [courseLoading, setCourseLoading] = useState(!initialCourse)
  const [pricing, setPricing] = useState([])
  const [pricingLoading, setPricingLoading] = useState(true)
  const [pricingError, setPricingError] = useState(null)
  const [currencyCode, setCurrencyCode] = useState('GBP')
  const [selectedAudience, setSelectedAudience] = useState('member')
  const didInitAudience = React.useRef(false)

  const heroImageUrl = course?.course_image_url || null

  useEffect(() => {
    let cancelled = false

    async function loadCourse() {
      setCourseLoading(true)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses`)
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_course')
        }

        if (!cancelled) {
          const list = Array.isArray(json?.data) ? json.data : []
          const found = list.find((c) => c.id === courseId) || null
          setCourse(found)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load course', err)
          setCourse(null)
        }
      } finally {
        if (!cancelled) setCourseLoading(false)
      }
    }

    if (!initialCourse) {
      loadCourse()
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, initialCourse, memberPortalApiGet])

  useEffect(() => {
    let cancelled = false

    async function loadPricing() {
      if (!courseId) return
      setPricingLoading(true)
      setPricingError(null)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/golf/courses/${courseId}/pricing`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'pricing_not_available')
        }

        if (!cancelled) {
          setPricing(Array.isArray(json?.pricing) ? json.pricing : [])
          if (json?.currency_code) {
            setCurrencyCode(json.currency_code)
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load pricing', err)
          setPricingError("portalPresentation.member_unable_to_load_course_rates_right_now")
        }
      } finally {
        if (!cancelled) setPricingLoading(false)
      }
    }

    loadPricing()

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, memberPortalApiGet])

  const pricingLookup = useMemo(() => buildPricingLookup(pricing), [pricing])
  const hasMemberRates = useMemo(() => {
    const groups = pricingLookup.member || {}
    return Object.values(groups).some((group) =>
      ['weekday', 'weekend'].some((day) =>
        Object.values(group?.[day] || {}).some((value) => typeof value === 'number' && !Number.isNaN(value))
      )
    )
  }, [pricingLookup])

  const hasVisitorRates = useMemo(() => {
    const groups = pricingLookup.visitor || {}
    return Object.values(groups).some((group) =>
      ['weekday', 'weekend'].some((day) =>
        Object.values(group?.[day] || {}).some((value) => typeof value === 'number' && !Number.isNaN(value))
      )
    )
  }, [pricingLookup])

  useEffect(() => {
    if (didInitAudience.current) return
    if (!hasMemberRates && !hasVisitorRates) return

    if (!hasMemberRates && hasVisitorRates) {
      setSelectedAudience('visitor')
    } else if (hasMemberRates) {
      setSelectedAudience('member')
    } else if (hasVisitorRates) {
      setSelectedAudience('visitor')
    }

    didInitAudience.current = true
  }, [hasMemberRates, hasVisitorRates])

  const activeRates = pricingLookup[selectedAudience] || {}
  const holeOptions = [9, 18].filter((holes) => activeRates?.[holes])
  const selectedDateKey = useMemo(
    () => normalizeDateKey(searchParams.get('date') || location.state?.date),
    [location.state, searchParams]
  )
  const teeTimesPath = selectedDateKey
    ? `/${clubCode}/golf/${courseId}?date=${selectedDateKey}`
    : `/${clubCode}/golf/${courseId}`
  const teeTimesState = course ? { course, date: selectedDateKey } : selectedDateKey ? { date: selectedDateKey } : undefined

  const loadingSkeleton = (
    <div className="mp-rate-skeleton">
      {[1, 2].map((key) => (
        <div key={key} className="mp-rate-skeleton__card" />
      ))}
    </div>
  )

  const emptyState = (
    <div className="mp-empty mp-empty--card"> {memberText("portalPresentation.member_rates_are_not_available_yet_please_contact_the_pro_shop")} </div>
  )

  return (
    <GolfCourseSplitLayout heroImageUrl={heroImageUrl}>
      <div className="mp-golf-header mp-rates-header">
        <div>
          <BackPillButton to={teeTimesPath} state={teeTimesState}> {memberText("portalPresentation.member_back_to_tee_times")} </BackPillButton>
          <p className="mp-eyebrow">{memberText("portalPresentation.member_course_rates")}</p>
          <h4 className="mp-panel__title">{course?.name || memberText("portalPresentation.member_course_rates")}</h4>
          {courseLoading && <p className="mp-text-muted">{memberText("portalPresentation.member_loading_course")}</p>}
        </div>
        {hasMemberRates && hasVisitorRates ? (
          <div className="mp-rate-toggle" role="tablist" aria-label={memberText("portalPresentation.member_select_rate_type")}>
            <button
              type="button"
              role="tab"
              aria-selected={selectedAudience === 'member'}
              className={`mp-rate-toggle__btn ${selectedAudience === 'member' ? 'active' : ''}`}
              onClick={() => setSelectedAudience('member')}
            > {memberText("portalPresentation.member_member_rates")} </button>
            <button
              type="button"
              role="tab"
              aria-selected={selectedAudience === 'visitor'}
              className={`mp-rate-toggle__btn ${selectedAudience === 'visitor' ? 'active' : ''}`}
              onClick={() => setSelectedAudience('visitor')}
            > {memberText("portalPresentation.member_guest_rates")} </button>
          </div>
        ) : null}
      </div>

      <div className="mp-golf-scroll-area">
        {pricingError ? <div className="mp-warning">{memberText(pricingError)}</div> : null}

        {pricingLoading ? (
          loadingSkeleton
        ) : holeOptions.length === 0 ? (
          emptyState
        ) : (
          <div className="mp-rate-grid">
            {holeOptions.map((holes) => (
              <div key={holes} className="mp-rate-card">
                <div className="mp-rate-card__header">
                  <div>
                    <p className="mp-eyebrow">{selectedAudience === 'visitor' ? memberText("portalPresentation.member_guest") : memberText("portalPresentation.member_member")} {memberText("portalPresentation.member_pricing")}</p>
                    <h5 className="mp-rate-card__title">{holes}{memberText("portalPresentation.member__hole")}</h5>
                  </div>
                  <span className="mp-chip">{currencyCode}</span>
                </div>

                <div className="mp-rate-matrix" role="table" aria-label={memberText("portalPresentation.member__holes_hole_pricing", { holes })}>
                  <div className="mp-rate-matrix__head" role="row">
                    <div role="columnheader" aria-hidden="true" />
                    <div role="columnheader">{memberText("golf.course.weekday")}</div>
                    <div role="columnheader">{memberText("golf.course.weekend")}</div>
                  </div>
                  <div className="mp-rate-matrix__row" role="row">
                    <div className="mp-rate-label" role="rowheader"> {memberText("golf.course.peak")} </div>
                    <div role="cell">
                      <RateCell
                        value={activeRates[holes]?.weekday?.peak}
                        currencyCode={currencyCode}
                      />
                    </div>
                    <div role="cell">
                      <RateCell
                        value={activeRates[holes]?.weekend?.peak}
                        currencyCode={currencyCode}
                      />
                    </div>
                  </div>
                  <div className="mp-rate-matrix__row" role="row">
                    <div className="mp-rate-label" role="rowheader"> {memberText("golf.course.offPeak")} </div>
                    <div role="cell">
                      <RateCell
                        value={activeRates[holes]?.weekday?.offpeak}
                        currencyCode={currencyCode}
                      />
                    </div>
                    <div role="cell">
                      <RateCell
                        value={activeRates[holes]?.weekend?.offpeak}
                        currencyCode={currencyCode}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </GolfCourseSplitLayout>
  )
}
